import React, { createContext, useContext, useState, useEffect } from 'react';
import { theme as defaultTheme, colors, lightTheme, darkTheme } from '../theme/colors';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [currentTheme, setCurrentTheme] = useState(() => {
    // Load theme from localStorage or default to dark
    const savedTheme = localStorage.getItem('selkirk-theme');
    return savedTheme ? JSON.parse(savedTheme) : 'dark';
  });

  const [customColors, setCustomColors] = useState(() => {
    // Load custom colors from localStorage
    const savedColors = localStorage.getItem('selkirk-custom-colors');
    return savedColors ? JSON.parse(savedColors) : {};
  });

  // Apply theme to document root
  useEffect(() => {
    const root = document.documentElement;
    const theme = currentTheme === 'light' ? lightTheme : darkTheme;
    const mergedColors = { ...colors.primary, ...customColors };

    // Set CSS custom properties
    root.style.setProperty('--color-primary-50', colors.primary[50]);
    root.style.setProperty('--color-primary-100', colors.primary[100]);
    root.style.setProperty('--color-primary-200', colors.primary[200]);
    root.style.setProperty('--color-primary-300', colors.primary[300]);
    root.style.setProperty('--color-primary-400', colors.primary[400]);
    root.style.setProperty('--color-primary-500', mergedColors[500] || colors.primary[500]);
    root.style.setProperty('--color-primary-600', colors.primary[600]);
    root.style.setProperty('--color-primary-700', colors.primary[700]);
    root.style.setProperty('--color-primary-800', colors.primary[800]);
    root.style.setProperty('--color-primary-900', colors.primary[900]);

    // Background colors
    root.style.setProperty('--bg-primary', theme.background.primary);
    root.style.setProperty('--bg-secondary', theme.background.secondary);
    root.style.setProperty('--bg-tertiary', theme.background.tertiary);
    root.style.setProperty('--bg-elevated', theme.background.elevated);

    // Text colors
    root.style.setProperty('--text-primary', theme.text.primary);
    root.style.setProperty('--text-secondary', theme.text.secondary);
    root.style.setProperty('--text-tertiary', theme.text.tertiary);
    root.style.setProperty('--text-inverse', theme.text.inverse);

    // Border colors
    root.style.setProperty('--border-primary', theme.border.primary);
    root.style.setProperty('--border-secondary', theme.border.secondary);
    root.style.setProperty('--border-accent', theme.border.accent);

    // Store theme preference
    localStorage.setItem('selkirk-theme', JSON.stringify(currentTheme));
    localStorage.setItem('selkirk-custom-colors', JSON.stringify(customColors));
  }, [currentTheme, customColors]);

  const toggleTheme = () => {
    setCurrentTheme(prev => prev === 'dark' ? 'light' : 'dark');
  };

  const updateCustomColor = (colorKey, value) => {
    setCustomColors(prev => ({
      ...prev,
      [colorKey]: value
    }));
  };

  const resetTheme = () => {
    setCustomColors({});
    setCurrentTheme('dark');
  };

  const themeValue = {
    // Current theme state
    theme: currentTheme,
    isDark: currentTheme === 'dark',
    isLight: currentTheme === 'light',
    
    // Theme colors
    colors: {
      ...colors,
      primary: { ...colors.primary, ...customColors }
    },
    
    // Theme actions
    toggleTheme,
    setTheme: setCurrentTheme,
    updateCustomColor,
    resetTheme,
    
    // Utility functions
    getThemeColor: (path) => {
      const keys = path.split('.');
      let value = { ...colors, primary: { ...colors.primary, ...customColors } };
      
      for (const key of keys) {
        value = value[key];
        if (value === undefined) break;
      }
      
      return value;
    },
    
    // Preset themes
    applyPreset: (preset) => {
      const presets = {
        default: {},
        copper: {
          500: '#b45309', // Darker copper
          400: '#d97706',
          300: '#f59e0b'
        },
        gold: {
          500: '#fbbf24', // Lighter gold
          400: '#fcd34d',
          300: '#fde68a'
        },
        blue: {
          500: '#3b82f6', // Professional blue
          400: '#60a5fa',
          300: '#93c5fd'
        }
      };
      
      setCustomColors(presets[preset] || {});
    }
  };

  return (
    <ThemeContext.Provider value={themeValue}>
      <div className={`theme-${currentTheme}`} data-theme={currentTheme}>
        {children}
      </div>
    </ThemeContext.Provider>
  );
};

export default ThemeProvider;
